import {
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
    Value,
} from "@material-ui/lab";
import { FieldInputProps, useField } from "formik";
import * as React from "react";

import { SelectInput, SelectInputMode, SelectInputProps } from "..";

export type SelectFieldProps<
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined
> = {
    setFieldValue?: (option: T) => any;
    getFieldOption?: (option: T, fieldValue: any) => boolean;
    mode: SelectInputMode;
} & SelectInputProps<T, Multiple, DisableClearable, FreeSolo> &
    Partial<Omit<FieldInputProps<any>, "onChange">>;

export const SelectField = <
    T,
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
>(
    props: SelectFieldProps<T, Multiple, DisableClearable, FreeSolo>
) => {
    const {
        name = props.id,
        checked,
        onChange,
        multiple,
        setFieldValue,
        getFieldOption,
        mode,
        ...passthrough
    } = props;
    const [field, meta, helpers] = useField(name);

    const handleChange = (
        event: React.ChangeEvent<any>,
        value: Value<T, Multiple, DisableClearable, FreeSolo>,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<T>
    ) => {
        const newValue = !!setFieldValue ? setFieldValue?.(value as T) : value;
        event.target.value = newValue;
        event.target.id = props.name;

        field.onChange(event);
        helpers.setValue(newValue);
        onChange?.(event, value, reason, details);
    };

    const formikValue =
        field.value === undefined || field.value === null ? "" : field.value;
    let value = formikValue;
    if (!!getFieldOption) {
        value =
            passthrough.options?.find(o => getFieldOption(o, formikValue)) ??
            null;
    }
    const {
        onBlur,
        onChange: fieldOnChange,
        multiple: formikMultiple,
        checked: formikChecked,
        ...fieldProps
    } = field;

    return (
        <SelectInput
            mode={mode}
            {...passthrough}
            {...fieldProps}
            value={value}
            error={!!meta.error}
            helperText={meta.error}
            onChange={handleChange}
        />
    );
};
